import { useState, useEffect } from 'react';
import { NetworkStatus } from 'apollo-client';
import type { QueryResult } from 'react-apollo';

import { expValEqualsNoExposure, manuallyLogExpExposure } from '@confluence/feature-experiments';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager';

type FeedStorageConfig<T> = {
	defaultValue: T;
	sessionStorageKey: string;
};

export const useFeedDropdownStorage = <TData, TVariables, TValue>(
	feedQuery: QueryResult<TData, TVariables>,
	config: FeedStorageConfig<TValue>,
): {
	value: TValue;
	setValue: (newValue: TValue) => void;
} => {
	const { defaultValue, sessionStorageKey } = config;
	const isTestCohort = expValEqualsNoExposure(
		'confluence_feeds_sorting_retention_experiment',
		'cohort',
		'test',
	);

	let initialValue = defaultValue;
	if (isTestCohort && !process.env.REACT_SSR && !window.__SSR_RENDERED__ && feedQuery.data) {
		const sessionValue = sessionStorage?.getItem(sessionStorageKey) as TValue;
		initialValue = sessionValue || defaultValue;

		if (sessionValue) {
			manuallyLogExpExposure('confluence_feeds_sorting_retention_experiment'); // log exposure log for test group
		}
	}

	if (!isTestCohort) {
		manuallyLogExpExposure('confluence_feeds_sorting_retention_experiment'); // log exposure for non-test group
	}

	const [value, setValue] = useState<TValue>(initialValue);

	// Update the session storage when value changes
	useEffect(() => {
		if (isTestCohort) {
			sessionStorage?.setItem(sessionStorageKey, value as string);
		}
	}, [sessionStorageKey, value, isTestCohort]);

	// Reset to default when refetching
	useEffect(() => {
		if (isTestCohort && feedQuery.networkStatus === NetworkStatus.refetch) {
			setValue(defaultValue);
		}
	}, [feedQuery.networkStatus, defaultValue, isTestCohort]);

	return { value, setValue };
};
